import React from "react";
import HeroSection from "../components/HeroSection.jsx";
import { heroSections } from "../constants/data.ts";
import images from "../constants/images.ts";

const frameworkSteps = [
  {
    title: "Candidate Assessment",
    description:
      "Every candidate begins with a comprehensive evaluation to identify strengths, learning needs, and career potential.",
    image: "/Images/Candidate%20Assessment.png",
    highlights: [
      "Aptitude Assessment",
      "Technical Skill Evaluation",
      "Communication Assessment",
      "Psychometric Analysis",
      "Domain Knowledge Screening",
    ],
  },
  {
    title: "Industry-Focused Training",
    description:
      "Candidates undergo intensive training aligned with current industry standards and employer expectations.",
    image: "/Images/Industry-Focused%20Training.png",
    highlights: [
      "Technical Skills Development",
      "Domain-Specific Knowledge",
      "Professional Communication",
      "Problem Solving",
      "Mock Interviews",
    ],
  },
  {
    title: "Internship & Real-Time Projects",
    description:
      "Candidates work on live or simulated industry projects under expert supervision to gain practical experience.",
    image: "/Images/Internship%20&%20Real-Time%20Projects.png",
    highlights: [
      "Real-Time Project Development",
      "Agile Development Practices",
      "Code Reviews",
      "Team Collaboration",
      "Portfolio Development",
    ],
  },
  {
    title: "Client Engagement & Placement",
    description:
      "Our placement team connects trained candidates with organizations that match their skills and career goals.",
    image: "/Images/Client%20Engagement%20&%20Placement.png",
    highlights: [
      "Resume Building",
      "Interview Preparation",
      "Technical Interview Guidance",
      "HR Interview Coaching",
      "Offer Coordination",
    ],
  },
];

const trainingPrograms = [
  {
    title: "Java Full Stack Development",
    description:
      "Build enterprise-grade web applications using modern Java technologies.",
    curriculum: [
      "Core Java",
      "Advanced Java",
      "Spring Framework",
      "Spring Boot",
      "REST APIs",
      "Hibernate",
      "MySQL & PostgreSQL",
      "Git & GitHub",
      "Real-Time Projects",
    ],
    opportunities: [
      "Java Developer",
      "Full Stack Developer",
      "Backend Developer",
    ],
  },
  {
    title: "Python Programming",
    description:
      "Develop strong programming fundamentals and build applications using Python.",
    curriculum: [
      "Python Fundamentals",
      "OOP Concepts",
      "Exception Handling",
      "Data Structures",
      "NumPy & Pandas",
      "REST API Development",
      "HTML, CSS & JavaScript",
      "Project Development",
    ],
    opportunities: [
      "Python Developer",
      "Backend Developer",
      "Automation Engineer",
    ],
  },
  {
    title: "Artificial Intelligence & Machine Learning",
    description:
      "Learn to build intelligent applications using modern AI and Machine Learning technologies.",
    curriculum: [
      "Python for AI",
      "Mathematics & Statistics",
      "Data Analysis",
      "Machine Learning Algorithms",
      "Deep Learning Fundamentals",
      "Model Training & Evaluation",
      "Cloud AI Services",
      "AI Project Development",
    ],
    opportunities: [
      "AI Engineer",
      "Machine Learning Engineer",
      "Data Scientist",
    ],
  },
  {
    title: "ERP Systems (SAP)",
    description:
      "Understand enterprise business processes and implement ERP solutions for organizations.",
    curriculum: [
      "ERP Fundamentals",
      "SAP Overview",
      "Finance Module",
      "HR Module",
      "Supply Chain Management",
      "Business Process Mapping",
      "System Configuration",
      "ERP Implementation",
    ],
    opportunities: [
      "SAP Consultant",
      "ERP Functional Consultant",
      "Business Analyst",
    ],
  },
];

const jobRoles = [
  {
    title: "Java Full Stack Developer",
    skills: [
      "Core & Advanced Java",
      "Spring Boot",
      "REST APIs",
      "MySQL & PostgreSQL",
      "Git",
      "Problem Solving",
    ],
  },
  {
    title: "Python Developer",
    skills: [
      "Core Python",
      "Object-Oriented Programming",
      "Pandas & NumPy",
      "REST API Development",
      "HTML, CSS & JavaScript",
      "GitHub",
    ],
  },
  {
    title: "AI & Machine Learning Engineer",
    skills: [
      "Machine Learning Algorithms",
      "Probability & Statistics",
      "Linear Algebra",
      "Model Development",
      "AI Frameworks",
      "Predictive Modeling",
    ],
  },
  {
    title: "ERP (SAP) Consultant",
    skills: [
      "ERP Implementation",
      "SAP Configuration",
      "Business Process Management",
      "System Integration",
      "Testing & Validation",
      "User Support",
    ],
  },
];

const benefits = [
  "Industry-Aligned Curriculum",
  "Experienced Corporate Trainers",
  "Real-Time Project Experience",
  "Personalized Mentorship",
  "Continuous Skill Assessments",
  "Professional Soft Skills Development",
  "Interview & Placement Preparation",
  "Strong Industry Network",
];

const placementServices = [
  "Resume Preparation",
  "Technical Interview Practice",
  "HR Interview Coaching",
  "Career Counseling",
  "Client Interview Coordination",
  "Job Matching",
  "Placement Assistance",
  "Post-Joining Support",
];

const industries = [
  "Information Technology",
  "Product Development",
  "FinTech",
  "Healthcare",
  "Networking & Telecommunications",
  "Cybersecurity",
  "Manufacturing",
  "Enterprise Software",
  "Retail & E-Commerce",
  "Banking & Financial Services",
];

const selectionSteps = [
  "Profile Screening",
  "Coding Test & Technical Assessment",
  "Communication Assessment",
  "HR Discussion",
  "Client Showcase & Placement",
];

const TalentContinuityManagement = () => {
  return (
    <main className="bg-background">
      <HeroSection {...heroSections.talentContinuityManagement} />

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <p className="mb-3 heading-main text-primary">
            Talent Continuity Management
          </p>
          <h2 className="heading-main mb-5 text-secondary">
            Transforming Talent into Future-Ready Professionals
          </h2>
          <div className="space-y-4">
            <p className="text-body">
              At Westlisoft, our Talent Continuity Management (TCM) program is
              designed to bridge the gap between academic learning and industry
              expectations. We don’t just train candidates—we develop skilled
              professionals who are ready to contribute from day one.
            </p>
            <p className="text-body">
              Our TCM framework combines assessment, industry-focused training,
              real-time project experience, mentorship, and placement support to
              create a seamless journey from learning to employment. By
              collaborating closely with businesses and educational
              institutions, we ensure organizations gain access to job-ready
              talent while candidates build successful careers.
            </p>
          </div>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 heading-main text-primary">
              Why Choose Our TCM Program?
            </p>
            <h2 className="heading-main text-secondary">
              A Structured Roadmap to Career Success
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <article className="rounded-card bg-white p-6 shadow-sm">
              <h3 className="mb-3 font-rubik text-xl font-semibold text-secondary">
                Industry-Ready Training
              </h3>
              <p className="text-body">
                Acquire practical knowledge through hands-on learning in Java,
                Python, AI & Machine Learning, ERP, and real-time projects
                aligned with current industry standards.
              </p>
            </article>
            <article className="rounded-card bg-white p-6 shadow-sm">
              <h3 className="mb-3 font-rubik text-xl font-semibold text-secondary">
                Career Acceleration
              </h3>
              <p className="text-body">
                Move confidently from campus to corporate with structured
                learning paths, professional mentoring, interview preparation,
                and soft skills development.
              </p>
            </article>
            <article className="rounded-card bg-white p-6 shadow-sm">
              <h3 className="mb-3 font-rubik text-xl font-semibold text-secondary">
                Placement Opportunities
              </h3>
              <p className="text-body">
                Gain access to employment opportunities with leading
                service-based companies, product organizations, fintech firms,
                healthcare providers, and enterprise clients.
              </p>
            </article>
            <article className="rounded-card bg-white p-6 shadow-sm">
              <h3 className="mb-3 font-rubik text-xl font-semibold text-secondary">
                Expert Mentorship
              </h3>
              <p className="text-body">
                Learn directly from experienced professionals who provide
                technical guidance, career coaching, and practical insights
                throughout your growth journey.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 heading-main text-primary">TCM Framework</p>
            <h2 className="heading-main text-secondary">
              A Step-by-Step Journey from Learning to Placement
            </h2>
          </div>

          <div className="space-y-8">
            {frameworkSteps.map((step, index) => (
              <article
                key={step.title}
                className="overflow-hidden rounded-card bg-white shadow-sm"
              >
                <div className="grid items-center gap-6 md:grid-cols-2">
                  <div className="p-8">
                    <div className="mb-4 flex items-start gap-4">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-secondary">
                        {index + 1}
                      </span>
                      <div>
                        <h3 className="font-rubik text-2xl font-semibold text-secondary">
                          {step.title}
                        </h3>
                        <p className="mt-3 text-body">{step.description}</p>
                      </div>
                    </div>

                    <ul className="mt-4 grid gap-2 text-sm font-light text-gray-700">
                      {step.highlights.map((item) => (
                        <li key={item} className="flex gap-3">
                          <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center justify-center bg-background p-6">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full max-h-[360px] object-contain"
                    />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 heading-main text-primary">Training Programs</p>
            <h2 className="heading-main text-secondary">
              Programs Built for Modern Careers
            </h2>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {trainingPrograms.map((program) => (
              <article
                key={program.title}
                className="rounded-card bg-white p-6 shadow-sm"
              >
                <h3 className="mb-3 font-rubik text-xl font-semibold text-secondary">
                  {program.title}
                </h3>
                <p className="mb-4 text-body">{program.description}</p>
                <div className="mb-4">
                  <h4 className="mb-2 font-rubik text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                    Curriculum Includes
                  </h4>
                  <ul className="grid gap-2 text-sm font-light text-gray-700 sm:grid-cols-2">
                    {program.curriculum.map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="mb-2 font-rubik text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                    Career Opportunities
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {program.opportunities.map((opportunity) => (
                      <span
                        key={opportunity}
                        className="rounded-full bg-background px-3 py-2 text-sm text-gray-700"
                      >
                        {opportunity}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 heading-main text-primary">
              Job Roles We Prepare You For
            </p>
            <h2 className="heading-main text-secondary">
              Careers Built on Industry-Relevant Skills
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {jobRoles.map((role) => (
              <article
                key={role.title}
                className="rounded-card bg-background p-6 shadow-sm"
              >
                <h3 className="mb-4 font-rubik text-xl font-semibold text-secondary">
                  {role.title}
                </h3>
                <ul className="space-y-2 text-sm font-light text-gray-700">
                  {role.skills.map((skill) => (
                    <li key={skill} className="flex gap-2">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      <span>{skill}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <p className="mb-3 heading-main text-primary">
                Selection Process
              </p>
              <h2 className="heading-main mb-5 text-secondary">
                A Transparent Path to Opportunity
              </h2>
              <ol className="space-y-4">
                {selectionSteps.map((step, index) => (
                  <li
                    key={step}
                    className="flex gap-3 rounded-card bg-white p-4 shadow-sm"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-secondary">
                      {index + 1}
                    </span>
                    <span className="font-rubik text-lg font-semibold text-secondary">
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-card bg-white p-6 shadow-sm">
              <p className="mb-3 heading-main text-primary">
                Placement Support
              </p>
              <h3 className="heading-main mb-4 text-secondary">
                Career Guidance Beyond Training
              </h3>
              <p className="mb-5 text-body">
                Our dedicated placement team works closely with hiring
                organizations to connect candidates with the right opportunities
                and ensure a smooth transition into professional roles.
              </p>
              <ul className="grid gap-2 text-sm font-light text-gray-700 sm:grid-cols-2">
                {placementServices.map((service) => (
                  <li key={service} className="flex gap-2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 heading-main text-primary">
              Why Westlisoft TCM?
            </p>
            <h2 className="heading-main text-secondary">
              Everything You Need to Build a Future-Ready Career
            </h2>
          </div>

          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <li
                key={benefit}
                className="flex min-h-20 items-center justify-center rounded-card bg-background px-4 py-4 text-center font-rubik text-sm font-light text-gray-700"
              >
                {benefit}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 heading-main text-primary">
              Industries We Serve
            </p>
            <h2 className="heading-main text-secondary">
              Opportunities Across Diverse Sectors
            </h2>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {industries.map((industry) => (
              <li
                key={industry}
                className="flex min-h-16 items-center justify-center rounded-card bg-white px-4 py-4 text-center font-rubik text-sm font-light text-gray-700 shadow-sm"
              >
                {industry}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 heading-main text-primary">
              Our Partners & Success Stories
            </p>
            <h2 className="heading-main text-secondary">
              Trusted by Organizations & Candidates
            </h2>
          </div>

          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <p className="mb-4 font-rubik text-lg font-semibold text-secondary">
                Hiring Partners
              </p>
              <div className="grid grid-cols-4 gap-4 items-center">
                {Object.values(images.clientLogos).map((src, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-center bg-background p-4 rounded"
                  >
                    <img
                      src={src}
                      alt={`partner-${idx}`}
                      className="h-12 object-contain"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-4 font-rubik text-lg font-semibold text-secondary">
                What Candidates Say
              </p>
              <div className="space-y-4">
                {[
                  {
                    name: "Ravi K",
                    role: "Java Trainee",
                    text: "TCM helped me transition to a developer role with real projects and interview support.",
                  },
                  {
                    name: "Sneha M",
                    role: "Data Analyst",
                    text: "Hands-on assignments and mentorship prepared me for interviews and real work.",
                  },
                ].map((t, i) => (
                  <blockquote
                    key={i}
                    className="rounded-card bg-background p-4"
                  >
                    <p className="text-body">"{t.text}"</p>
                    <p className="mt-3 font-semibold text-secondary">
                      {t.name}{" "}
                      <span className="text-sm font-light">— {t.role}</span>
                    </p>
                  </blockquote>
                ))}
              </div>

              <div className="mt-6 rounded-card bg-white p-6 shadow-sm">
                <h3 className="mb-2 font-rubik text-lg font-semibold text-secondary">
                  Ready to Join TCM?
                </h3>
                <p className="text-body mb-4">
                  Contact our placement team to learn about upcoming batches,
                  partnerships, and hiring opportunities.
                </p>
                <div className="flex gap-3">
                  <a
                    href="/contact"
                    className="rounded bg-primary px-5 py-2 text-secondary font-semibold"
                  >
                    Contact Us
                  </a>
                  <a
                    href="mailto:careers@westlisoft.com"
                    className="rounded border border-primary px-5 py-2 text-primary"
                  >
                    Email Careers
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-5xl text-center">
          <h2 className="heading-main mb-4 text-primary">
            Start Your Career with Confidence
          </h2>
          <p className="text-body mx-auto max-w-4xl">
            Join Westlisoft TCM and take the first step toward a rewarding and
            future-ready career. Through structured learning, real-time
            experience, expert mentorship, and dedicated placement support, we
            empower candidates with the knowledge, confidence, and skills needed
            to excel in the workplace.
          </p>
        </div>
      </section>
    </main>
  );
};

export default TalentContinuityManagement;
